import React, { useState, useEffect } from 'react';
import portalApi, { getPortalErrorMessage } from '@/lib/portalApi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  MessageSquare, Send, RefreshCw, Plus, Clock, CheckCircle, XCircle,
  Paperclip, ChevronDown, ChevronUp, User, Calendar
} from 'lucide-react';

const statusColors = {
  OPEN: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  RESPONDED: 'bg-green-500/10 text-green-600 border-green-500/20',
  CLOSED: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
};

const statusIcons = {
  OPEN: Clock,
  RESPONDED: CheckCircle,
  CLOSED: XCircle,
};

function MessageCard({ message, expanded, onToggle }) {
  const StatusIcon = statusIcons[message.status] || Clock;
  const hasResponse = message.response || message.status === 'RESPONDED' || message.status === 'CLOSED';

  return (
    <Card className={`transition-colors ${message.status === 'OPEN' ? 'border-blue-500/30' : ''}`} data-testid={`message-${message.id}`}>
      <CardContent className="p-4">
        {/* Header */}
        <div 
          className="flex items-start justify-between cursor-pointer"
          onClick={onToggle}
        >
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
              message.status === 'RESPONDED' ? 'bg-green-500/10' : 
              message.status === 'CLOSED' ? 'bg-gray-500/10' : 'bg-blue-500/10'
            }`}>
              <StatusIcon className={`h-5 w-5 ${
                message.status === 'RESPONDED' ? 'text-green-500' : 
                message.status === 'CLOSED' ? 'text-gray-500' : 'text-blue-500'
              }`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={statusColors[message.status] || ''}>
                  {message.status}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {new Date(message.createdAt).toLocaleDateString('en-AU', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
              <p className={`mt-2 text-sm ${expanded ? '' : 'line-clamp-2'}`}>
                {message.message}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="flex-shrink-0 ml-2">
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>

        {/* Expanded Content */}
        {expanded && (
          <div className="mt-4 pt-4 border-t space-y-4">
            {/* Full message */}
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Your Message</p>
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-sm whitespace-pre-wrap">{message.message}</p>
              </div>
            </div>

            {/* Attachments */}
            {message.attachments && message.attachments.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Attachments</p>
                <div className="flex flex-wrap gap-2">
                  {message.attachments.map((att, idx) => (
                    <a
                      key={idx}
                      href={att.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg text-sm hover:bg-muted transition-colors"
                    >
                      <Paperclip className="h-3.5 w-3.5" />
                      {att.fileName}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Response */}
            {hasResponse && message.response && (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Response from Care Team</p>
                <div className="p-3 bg-green-500/5 border border-green-500/20 rounded-lg">
                  <p className="text-sm whitespace-pre-wrap">{message.response}</p>
                  {message.respondedAt && (
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(message.respondedAt).toLocaleDateString('en-AU', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Response Attachments */}
            {message.responseAttachments && message.responseAttachments.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Response Attachments</p>
                <div className="flex flex-wrap gap-2">
                  {message.responseAttachments.map((att, idx) => (
                    <a
                      key={idx}
                      href={att.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg text-sm hover:bg-muted transition-colors"
                    >
                      <Paperclip className="h-3.5 w-3.5" />
                      {att.fileName}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MessageSkeleton() {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="flex-1">
            <Skeleton className="h-5 w-24 mb-2" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4 mt-1" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function NewMessageDialog({ onSuccess }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim() || message.trim().length < 5) {
      toast.error('Message must be at least 5 characters');
      return;
    }

    setLoading(true);
    try {
      await portalApi.post('/portal/messages', {
        message: message.trim(),
        attachments: []
      });
      toast.success('Message sent successfully');
      setMessage('');
      setOpen(false);
      onSuccess();
    } catch (err) {
      toast.error(getPortalErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-message-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Message
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Send a Message</DialogTitle>
          <DialogDescription>
            Send a message to your care team. They will respond as soon as possible.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message here..."
                rows={6}
                required
                data-testid="message-input"
              />
              <p className="text-xs text-muted-foreground">
                {message.length}/5000 characters
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || message.trim().length < 5} data-testid="send-message-btn">
              {loading ? 'Sending...' : 'Send Message'}
              <Send className="h-4 w-4 ml-2" />
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function PortalMessagesPage() {
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all');

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await portalApi.get('/portal/messages');
      setMessages(res.data.items || []);
    } catch (err) {
      toast.error(getPortalErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const filteredMessages = messages.filter(msg => {
    if (filter === 'all') return true;
    return msg.status === filter;
  });

  const openCount = messages.filter(m => m.status === 'OPEN').length;
  const respondedCount = messages.filter(m => m.status === 'RESPONDED').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Messages</h1>
          <p className="text-muted-foreground">Communicate with your care team</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchMessages} disabled={loading} data-testid="refresh-messages">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <NewMessageDialog onSuccess={fetchMessages} />
        </div>
      </div>

      {/* Stats */}
      {!loading && messages.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <Card className="cursor-pointer hover:border-primary/30" onClick={() => setFilter('all')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{messages.length}</p>
              <p className="text-xs text-muted-foreground">Total Messages</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-blue-500/30" onClick={() => setFilter('OPEN')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-500">{openCount}</p>
              <p className="text-xs text-muted-foreground">Awaiting Response</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-green-500/30" onClick={() => setFilter('RESPONDED')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-500">{respondedCount}</p>
              <p className="text-xs text-muted-foreground">Responded</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {[
          { key: 'all', label: 'All' },
          { key: 'OPEN', label: 'Open' },
          { key: 'RESPONDED', label: 'Responded' },
          { key: 'CLOSED', label: 'Closed' },
        ].map((tab) => (
          <Button
            key={tab.key}
            variant={filter === tab.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(tab.key)}
            data-testid={`filter-${tab.key}`}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Messages List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <MessageSkeleton key={i} />)}
        </div>
      ) : filteredMessages.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-lg font-medium">No Messages</p>
            <p className="text-sm text-muted-foreground mb-4">
              {filter !== 'all' ? 'No messages with this status' : 'Start a conversation with your care team'}
            </p>
            <NewMessageDialog onSuccess={fetchMessages} />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredMessages.map((message) => (
            <MessageCard
              key={message.id}
              message={message}
              expanded={expandedId === message.id}
              onToggle={() => setExpandedId(expandedId === message.id ? null : message.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
